import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { birdToast } from '../../shared/components/core-ui/toast';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { HrDayBreakdown } from '../../shared/models/hr.model';
import { createHrLeaveRequest } from '../../shared/services/data/hr.data';
import { HrLeaveTypeUiModel } from '../../shared/ui-models/hr.ui-model';
import { HrDialog } from './hr-dialog.component';
import {
  BREAKDOWNS,
  BTN_PRIMARY,
  BTN_SECONDARY,
  FIELD_ERROR,
  INPUT,
  LABEL,
  LeaveFormValues,
  TEXTAREA,
  todayIso,
  validateLeave
} from './hr.util';

const EMPTY_FORM = (): LeaveFormValues => ({
  leaveTypeId: '',
  startDate: todayIso(),
  startDateBreakdown: 'full_day',
  endDate: todayIso(),
  endDateBreakdown: 'full_day',
  description: ''
});

export const LeaveRequestModal: React.FC<{
  leaveTypes: HrLeaveTypeUiModel[];
  onClose: () => void;
  onCreated: () => void;
}> = ({ leaveTypes, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<LeaveFormValues>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const errors = useMemo(() => validateLeave(values), [values]);
  const selectedType = leaveTypes.find((type) => type.id === values.leaveTypeId);
  const set = (patch: Partial<LeaveFormValues>) => setValues((prev) => ({ ...prev, ...patch }));
  const errorFor = (field: keyof LeaveFormValues) =>
    touched && errors[field] ? (
      <span className={FIELD_ERROR}>{t(`HR_MODULE.FORM.ERRORS.${errors[field]}`)}</span>
    ) : null;

  const submit = () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    createHrLeaveRequest({
      leaveTypeId: values.leaveTypeId,
      startDate: values.startDate,
      startDateBreakdown: values.startDateBreakdown,
      endDate: values.endDate,
      endDateBreakdown: values.endDateBreakdown,
      description: values.description.trim()
    }).then((result) => {
      setSaving(false);
      if (isErrorInfo(result)) {
        birdToast.error(
          result.message || t('HR_MODULE.TOASTS.FAILED'),
          t('HR_MODULE.TOASTS.TITLE')
        );
        return;
      }
      birdToast.success(t('HR_MODULE.TOASTS.LEAVE_CREATED'), t('HR_MODULE.TOASTS.TITLE'));
      onCreated();
    });
  };

  const breakdownSelect = (
    value: HrDayBreakdown,
    onChange: (next: HrDayBreakdown) => void,
    label: string
  ) => (
    <label className={LABEL}>
      {label}
      <select
        className={INPUT}
        value={value}
        onChange={(e) => onChange(e.target.value as HrDayBreakdown)}
      >
        {BREAKDOWNS.map((b) => (
          <option key={b} value={b}>
            {t(`HR_MODULE.BREAKDOWN.${b}`)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <HrDialog
      title={t('HR_MODULE.FORM.LEAVE_TITLE')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            {t('HR_MODULE.ACTIONS.CLOSE')}
          </button>
          <button type="button" className={BTN_PRIMARY} disabled={saving} onClick={submit}>
            {t('HR_MODULE.ACTIONS.SUBMIT')}
          </button>
        </>
      }
    >
      <label className={LABEL}>
        {t('HR_MODULE.FORM.LEAVE_TYPE')}
        <select
          className={INPUT}
          value={values.leaveTypeId}
          onChange={(e) => set({ leaveTypeId: e.target.value })}
        >
          <option value="">{t('HR_MODULE.FORM.LEAVE_TYPE_PLACEHOLDER')}</option>
          {leaveTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {selectedType ? (
          <span className="text-[12px] font-gilroy-regular text-neutral-300">
            {t('HR_MODULE.FORM.AVAILABLE_DAYS', { days: selectedType.availableDays })}
          </span>
        ) : null}
        {errorFor('leaveTypeId')}
      </label>
      <div className="grid gap-[16px] sm:grid-cols-2">
        <label className={LABEL}>
          {t('HR_MODULE.FORM.START_DATE')}
          <input
            type="date"
            className={INPUT}
            value={values.startDate}
            onChange={(e) => {
              const startDate = e.target.value;
              // Moving the start past the end drags the end along: the common case is a
              // single day, and an end left behind is an error the user did not make.
              set(
                values.endDate && startDate > values.endDate
                  ? { startDate, endDate: startDate }
                  : { startDate }
              );
            }}
          />
          {errorFor('startDate')}
        </label>
        <label className={LABEL}>
          {t('HR_MODULE.FORM.END_DATE')}
          <input
            type="date"
            className={INPUT}
            min={values.startDate || undefined}
            value={values.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
          />
          {errorFor('endDate')}
        </label>
        {breakdownSelect(
          values.startDateBreakdown,
          (startDateBreakdown) => set({ startDateBreakdown }),
          t('HR_MODULE.FORM.START_BREAKDOWN')
        )}
        {breakdownSelect(
          values.endDateBreakdown,
          (endDateBreakdown) => set({ endDateBreakdown }),
          t('HR_MODULE.FORM.END_BREAKDOWN')
        )}
      </div>
      <label className={LABEL}>
        {t('HR_MODULE.FORM.DESCRIPTION')}
        <textarea
          className={TEXTAREA}
          placeholder={t('HR_MODULE.FORM.DESCRIPTION_PLACEHOLDER')}
          value={values.description}
          onChange={(e) => set({ description: e.target.value })}
        />
        {errorFor('description')}
      </label>
    </HrDialog>
  );
};
